import React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Text } from "src/library/components/Text";
import { ActionsPopOut } from "src/library/components/ActionsPopOut/index";
import { DayRangePicker } from "src/library/components/DayRangePicker";
import { useOutsideItemClick } from "src/library/hooks";

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface SelectedDateRangeProps {
  onDateConfirmAction: (range: DateRange) => void;
  range?: DateRange;
  maxDate?: Date;
  minDate?: Date;
  // number of days allowed for the range
  maxRangeDays?: number;
}

/**
 * An input component that enables user to select a date range, and displays result inside an input looking component
 */
const SelectedDateRange: React.FunctionComponent<SelectedDateRangeProps> = ({
  range, maxDate, minDate, onDateConfirmAction, maxRangeDays
}) => {
  const [ startDate, setStartDate ] = React.useState<Date | null>(range?.start || null);
  const [ endDate, setEndDate ] = React.useState<Date | null>(range?.end || null);
  const [ showDateRangePicker, setShowDateRangePicker ] = React.useState<boolean>(false);
  const divRef = React.useRef<HTMLDivElement>(null);
  const dateFormat = "DD/MM/YYYY";

  const alignWithRange = React.useCallback((alignRange?: DateRange) => {
    setStartDate(alignRange?.start || null);
    setEndDate(alignRange?.end || null);
  }, [ ]);

  // Allow parent to override range
  React.useEffect(() => {
    alignWithRange(range);
  }, [ alignWithRange, range ]);
  // When clicking outside component, reset the date range display to the original range
  useOutsideItemClick(divRef, () => alignWithRange(range), [ range, alignWithRange ]);

  /**
   * Dynamically chooses the min date based on max date range and the current date selection
   */
  const allowedMaxDate: Date | undefined = React.useMemo(() => {
    // use max date unless a start date is set
    if (startDate) {
      // when there is a max day range set
      if (maxRangeDays) {
        const maxRange = moment.utc(startDate).add(maxRangeDays, "days");

        // no max date then use the range
        if (!maxDate) {
          return maxRange.toDate();
          // otherwise check which is earlier out of the 2
        } else if (maxRange.isBefore(moment.utc(maxDate), "days")) {
          return maxRange.toDate();
        }
      }
    }

    return maxDate;
  }, [
    maxDate,
    maxRangeDays,
    startDate
  ]);

  /**
   * Dynamically chooses the min date based on min date range and the current date selection
   */
  const allowedMinDate: Date | undefined = React.useMemo(() => {
    // use end date for calc if we have one set
    if (endDate) {
      if (maxRangeDays) {
        const minRange = moment.utc(endDate).subtract(maxRangeDays, "days");

        // if no min date use the range date
        if (!minDate) {
          return minRange.toDate();
          // otherwise check which is later out of the 2 and use that
        } else if (minRange.isAfter(moment.utc(minDate), "days")) {
          return minRange.toDate();
        }
      }
      // otherwise do it on start date (the first date they select)
    } else if (startDate) {
      if (maxRangeDays) {
        const minRange = moment.utc(startDate).subtract(maxRangeDays, "days");

        if (!minDate) {
          return minRange.toDate();
          // otherwise check which is later out of the 2 and use that
        } else if (minRange.isAfter(moment.utc(minDate), "days")) {
          return minRange.toDate();
        }
      }
    }

    return minDate;
  }, [
    endDate,
    maxRangeDays,
    minDate,
    startDate
  ]);

  /**
   * Dont allow submit if the date range falls outside the min/max
   */
  const disableConfirm: boolean = React.useMemo(() => {
    // when no dates set then dont allow
    if (!(startDate && endDate)) {
      return true;
    } else {
      // if the user selects dats outside the allowd range then block confirm
      if (moment.utc(startDate).isBefore(allowedMinDate, "days")) {
        return true;
      } else if (moment.utc(endDate).isAfter(allowedMaxDate, "days")) {
        return true;
      }

      return false;
    }
  }, [
    allowedMaxDate,
    allowedMinDate,
    endDate,
    startDate
  ]);

  return (
    <StyledWrapper className="selected-date-range">
      <div
        className="date-input-wrapper"
        onClick={() => setShowDateRangePicker(true)}
      >
        <div className="displayed-value">
          <Icon
            size={18}
            icon={Icons.CALENDAR}
            $fillColor="midGrey"
          />

          <Text>
            {startDate && endDate ?
              `${moment.utc(startDate).format(dateFormat)} - ${moment.utc(endDate).format(dateFormat)}` :
              "Please Select Date Range"}
          </Text>
        </div>
      </div>

      {showDateRangePicker && (
        <div style={{ position: "relative" }}>
          <ActionsPopOut
            directionHorizontal="right"
            directionVertical="bottom"
            ref={divRef}
            handleClose={() => setShowDateRangePicker(false)}
            disableAction={disableConfirm}
            action={() => onDateConfirmAction({
              start: startDate,
              end: endDate
            })}
            steps={[
              {
                allowNext: false,
                component: (
                  <DayRangePicker
                    maxRangeDays={maxRangeDays}
                    maxDate={allowedMaxDate || moment.utc().add(10, "years").toDate()}
                    minDate={allowedMinDate || moment.utc().subtract(10, "years").toDate()}
                    date={startDate && moment.utc(startDate).toDate() || new Date()}
                    handleDateChange={({ from, to }) => {
                      if (from) {
                        setStartDate(from);
                      }

                      if (to) {
                        setEndDate(to);
                      }
                    }}
                  />
                )
              }
            ]}
          />
        </div>
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.lightGrey};
    border-radius: ${theme.borderRadius};
    height: 40px;;
    padding: 0 ${theme.spacing.padding.md};
    
    :hover {
      cursor: pointer;
      border-color: ${theme.colors.midGrey};
    }

    .date-input-wrapper {
      height: 100%;
      width:100%;
      display: flex;
      justify-content: start;
      align-items: center;
    }

    .displayed-value {
      display: flex;
      align-items: center;

      svg {
        margin-right: ${theme.spacing.margins.md};
      }
    }
  `}
`;

export default SelectedDateRange;
